// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "../../components/PageHeader";
import PageTitle from "../../components/PageTitle";
import PageBody from "../../components/PageBody";
import { RouteComponentProps } from "@reach/router";
import { requestGetStatistics } from "../../../state/statistics/StatisticsEffects";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Row,
  Select,
  Space,
  Statistic,
  Table
} from "antd";
import locale from "antd/es/date-picker/locale/fr_FR";
import { AuthentifiedUser } from "../../../api/backend-openapi";
import { useStore } from "effector-react";
import { authentifiedUserStore } from "../../../state/auth/AuthStore";
import { BranchesStore } from "../../../state/branches/BranchesStore";
import moment from "moment";
import { displayError } from "../../../utils/displayError";

interface NewsView {
  newsId: string;
  title: string;
  count: number;
}

interface StatisticResults {
  nbConnections: number;
  nbInstallations: number;
  nbsNewsViews: NewsView[];
  nbScreenViews: number;
}

const audienceList = [
  { label: "Tous", value: "tous" },
  { label: "CAC", value: "cac" },
  { label: "Collabs", value: "cac-collaborateur" }
];

const StatisticsScreen: React.FunctionComponent<RouteComponentProps> = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [results, setResults] = useState<StatisticResults | undefined>(
    undefined
  );
  const authenticatedUser: AuthentifiedUser | null = useStore(
    authentifiedUserStore
  );
  const branchList = useStore(BranchesStore);

  const [form] = Form.useForm();
  const dateFormat = "YYYY-MM-DD";
  const columns = [
    { title: t("statistics.most_viewed_news.count"), dataIndex: "count" },
    { title: t("statistics.most_viewed_news.news_title"), dataIndex: "title" }
  ];

  const handleSubmit = (values: any) => {
    if (!isLoading) {
      setIsLoading(true);

      requestGetStatistics({
        dto: {
          start: values.startDate.format(dateFormat),
          end: values.endDate.format(dateFormat),
          crcc: values?.branch ? values.branch.split("-").pop() : undefined,
          profile: values.audience === "tous" ? undefined : values.audience
        }
      });
    }
  };

  useEffect(() => {
    return requestGetStatistics.done.watch(({ result }) => {
      setIsLoading(false);
      if (result.ok) {
        if (result.data) {
          setResults(result.data);
        } else {
          displayError(result.errorMessage || "Error!", false);
        }
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  const sortDescendant = (newsViewedList: NewsView[]) =>
    newsViewedList.sort((news1, news2) =>
      news1.count < news2.count ? 1 : news1.count > news2.count ? -1 : 0
    );

  return (
    <>
      <PageHeader>
        <PageTitle title={t("statistics.title")} />
      </PageHeader>
      <PageBody>
        <Form
          layout={"vertical"}
          form={form}
          initialValues={{
            branch: authenticatedUser?.branch || undefined,
            audience: "tous",
            startDate: moment().subtract(7, "d"),
            endDate: moment()
          }}
          onFinish={handleSubmit}
          className={"mb-3"}
        >
          <div className="d-flex flex-row justify-content-end align-items-stretch flex-wrap">
            <div className="flex-grow-1 d-flex align-items-center">
              <Divider orientation="left" className="mb-0 mb-sm-3 mt-3">
                <h3 className="text-secondary mb-0">
                  {t("news_list.search_title")}
                </h3>
              </Divider>
            </div>
            <div className="d-flex align-items-center divider-sm-transparent">
              <Divider orientation="right" className="mt-2 mt-sm-3 mb-3">
                <Button
                  type="default"
                  className="btn-tertiary"
                  htmlType="submit"
                  loading={isLoading}
                >
                  {t("statistics.form.submit")}
                </Button>
              </Divider>
            </div>
          </div>
          <Row align={"bottom"} gutter={24}>
            <Col xs={24} sm={16} lg={8}>
              <Form.Item
                label={<span>{t("statistics.form.branch")}</span>}
                name="branch"
              >
                <Select
                  loading={isLoading}
                  disabled={!!authenticatedUser?.branch}
                  allowClear
                >
                  {Object.keys(branchList).map(
                    key =>
                      key.includes("crcc") && (
                        <Select.Option key={key} value={key}>
                          {branchList[key].label}
                        </Select.Option>
                      )
                  )}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8} lg={4}>
              <Form.Item
                label={<span>{t("statistics.form.audience")}</span>}
                name="audience"
              >
                <Select loading={isLoading} defaultValue={"tous"}>
                  {audienceList.map(item => (
                    <Select.Option
                      key={`stat_select_audience_${item.value}`}
                      value={item.value}
                    >
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item
                label={<span>{t("statistics.form.startDate")}</span>}
                name="startDate"
                rules={[
                  {
                    required: true,
                    message: t("statistics.form.required")
                  }
                ]}
              >
                <DatePicker locale={locale} size={"large"} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item
                label={<span>{t("statistics.form.endDate")}</span>}
                name="endDate"
              >
                <DatePicker locale={locale} size={"large"} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        {!isLoading && results && (
          <>
            <Space
              direction="vertical"
              size="middle"
              style={{ display: "flex" }}
            >
              <Row align={"bottom"} gutter={24}>
                <Col xs={8}>
                  <Card>
                    <Statistic
                      title={
                        <h3 className="text-secondary mb-0">
                          {t("statistics.nb_connections")}
                        </h3>
                      }
                      value={results.nbConnections}
                    />
                  </Card>
                </Col>
                <Col xs={8}>
                  <Card>
                    <Statistic
                      title={
                        <h3 className="text-secondary mb-0">
                          {t("statistics.nb_installations")}
                        </h3>
                      }
                      value={results.nbInstallations}
                    />
                  </Card>
                </Col>
                <Col xs={8}>
                  <Card>
                    <Statistic
                      title={
                        <h3 className="text-secondary mb-0">
                          {t("statistics.nb_screen_views")}
                        </h3>
                      }
                      value={results.nbScreenViews}
                    />
                  </Card>
                </Col>
              </Row>
              <Divider orientation="left">
                <h3 className="text-secondary mb-0">
                  {t("statistics.most_viewed_news.title")}
                </h3>
              </Divider>
              <Table
                columns={columns}
                dataSource={sortDescendant(results.nbsNewsViews)}
                pagination={false}
              />
            </Space>
          </>
        )}
      </PageBody>
    </>
  );
};

export default StatisticsScreen;
